import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_ORIGIN = "https://auth.emergentagent.com/";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const navigate = useNavigate();

  // Handle Emergent OAuth callback: URL fragment #session_id=... after Google redirect.
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    // Clear the fragment so it isn't re-processed
    window.history.replaceState(null, "", window.location.pathname);

    setOauthLoading(true);
    api.post("/admin/google-session", { session_id: sessionId })
      .then(() => {
        toast.success("Signed in with Google.");
        navigate("/admin");
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed.");
      })
      .finally(() => setOauthLoading(false));
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // The backend sets an httpOnly `admin_token` cookie on success — no localStorage.
      await api.post("/admin/login", { email, password });
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/login";
    window.location.href = `${EMERGENT_AUTH_ORIGIN}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (oauthLoading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6" data-testid="admin-oauth-loading">
        <div className="text-center text-ivory">
          <div className="overline text-gold">Signing you in</div>
          <div className="font-serif text-3xl mt-3">Verifying with Google&hellip;</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6 py-12" data-testid="admin-login-page">
      <div className="parchment-overlay" style={{ position: 'fixed' }} />
      <div className="relative max-w-md w-full">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="1123 Publishing Company Logo" className="w-20 h-20 mx-auto border border-gold/30 mb-5" />
          <div className="overline text-gold">1123 Publishing</div>
          <h1 className="font-serif text-4xl text-ivory mt-2">Editorial Desk</h1>
        </div>

        <div className="bg-white border border-gold/30 p-8 space-y-5">
          <button
            type="button"
            onClick={startGoogleSignIn}
            data-testid="admin-google-signin"
            className="w-full flex items-center justify-center gap-3 bg-white border border-navy/25 py-3 text-sm text-navy hover:bg-parchment transition-colors"
          >
            <GoogleGlyph />
            <span className="tracking-[0.04em]">Sign in with Google</span>
          </button>

          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-navy/40">
            <span className="flex-1 h-px bg-navy/15" />
            <span>or use password</span>
            <span className="flex-1 h-px bg-navy/15" />
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="overline">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email"
                className="w-full mt-2 bg-parchment border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="overline">Password</label>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password"
                className="w-full mt-2 bg-parchment border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-submit"
              className="w-full bg-navy text-ivory py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal disabled:opacity-60">
              {loading ? "Signing in..." : "Enter"}
            </button>
          </form>

          <Link to="/" className="block text-center text-xs text-navy/60 uppercase tracking-[0.2em] hover:text-gold">&larr; Back to the imprint</Link>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
